import { useAuth } from '@/auth/AuthContext'
import { useOrganization } from '@/hooks/useOrganization'
import { requestErrorMessage } from '@/helpers/apiClient'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { Badge } from '@/components/ui/badge'
import { BILLING_STATE_LABELS, billingDeadline, billingDateLabel, dollars } from '@/lib/billing'
import { BILLING_LIVE, PRICE_PER_VENUE_CENTS } from '@lib/constants/billing'
import { Navigate } from 'react-router-dom'
import { BillingActions } from '@/components/billing/BillingActions'

/** One labeled fact on the page. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  )
}

/**
 * Where the organization stands on billing, for its organization admins.
 *
 * Everyone sees the same facts — state, venues, what the bill comes to, who pays
 * — and only the billing owner can act on them.
 */
export function BillingPage() {
  // Not offered before billing is live; a bookmarked or typed URL goes home.
  if (!BILLING_LIVE) return <Navigate to="/" replace />
  return <BillingPageContent />
}

function BillingPageContent() {
  const { user } = useAuth()
  const organizationQuery = useOrganization()

  if (organizationQuery.isPending) return <LoadingState label="Loading billing…" />
  if (organizationQuery.isError) {
    return (
      <ErrorState message={requestErrorMessage(organizationQuery.error)} onRetry={() => void organizationQuery.refetch()} />
    )
  }
  const organization = organizationQuery.data
  const { billing, billingOwner } = organization
  const isOwner = billingOwner?.id === user?.id
  const deadline = billingDeadline(billing)
  const bill = billing.activeVenueCount * PRICE_PER_VENUE_CENTS

  return (
    <PageContainer className="space-y-6">
      <PageHeader title="Billing" description={`${dollars(PRICE_PER_VENUE_CENTS)} per active venue, per month.`} />
      <dl className="grid gap-5 rounded-md border border-line p-5 sm:grid-cols-2">
        <Fact label="Status">
          <Badge tone={billing.state === 'past_due' || billing.state === 'comp_ending' ? 'warning' : 'neutral'}>
            {BILLING_STATE_LABELS[billing.state]}
          </Badge>
        </Fact>
        {deadline ? <Fact label={deadline.label}>{billingDateLabel(deadline.at)}</Fact> : null}
        <Fact label="Active venues">{billing.activeVenueCount}</Fact>
        <Fact label="Monthly total">
          {billing.state === 'comped' ? 'Complimentary' : `${dollars(bill)} (${String(billing.activeVenueCount)} × ${dollars(PRICE_PER_VENUE_CENTS)})`}
        </Fact>
        <Fact label="Billing owner">
          {billingOwner ? `${billingOwner.name} (${billingOwner.email})` : 'Not chosen yet'}
        </Fact>
      </dl>
      {isOwner ? (
        <BillingActions organization={organization} />
      ) : (
        <p className="text-sm text-ink-muted">
          Only the billing owner can change the card, see invoices or cancel. They can hand billing to another
          organization admin from the Users page.
        </p>
      )}
    </PageContainer>
  )
}
